/**
 * Visualization Constants
 * Shared constants for Parsant D3 visualizations following brand guidelines
 */

// Brand Colors - Per Style Guide v3.0
export const COLORS = {
  // Primary Brand Colors
  voidBlack: '#050508',
  signalWhite: '#E8E6E3',
  commandAmber: '#C9A227',
  focusTeal: '#0EA5E9',
  steel: '#7A8494',
  successEmerald: '#2EA043',

  // Data Visualization Colors
  viz: {
    primary: '#C9A227',
    secondary: '#7A8494',
    positive: '#2EA043',
    negative: '#DA3633',
    neutral: '#6B7280',
    highlight: 'rgba(201, 162, 39, 0.2)',
  },

  // Chart-specific
  grid: 'rgba(122, 132, 148, 0.1)',
  gridDark: 'rgba(122, 132, 148, 0.15)',
  border: 'rgba(122, 132, 148, 0.2)',
  
  // Semantic
  trend: {
    up: '#DA3633',
    down: '#2EA043',
    flat: '#6B7280',
  },
} as const;

// Typography - Per Brand Guide
export const TYPOGRAPHY = {
  fontFamily: {
    sans: "'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif",
    mono: "'JetBrains Mono', 'SF Mono', 'Monaco', monospace",
  },
  
  fontSize: {
    xs: '10px',
    sm: '11px',
    base: '12px',
    md: '13px',
    lg: '14px',
    xl: '16px',
    '2xl': '20px',
  },
  
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
} as const;

// Animation Constants
export const ANIMATION = {
  duration: {
    instant: 0,
    fast: 150,
    normal: 300,
    slow: 600,
    verySlow: 1000,
    stagger: 50,
  },
  
  easing: {
    linear: 'linear',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
  },
} as const;

// Default Margins
export const MARGINS = {
  small: { top: 20, right: 20, bottom: 30, left: 40 },
  medium: { top: 40, right: 40, bottom: 50, left: 60 },
  large: { top: 60, right: 80, bottom: 60, left: 80 },
} as const;

// Chart Sizing
export const CHART_SIZES = {
  small: { width: 400, height: 250 },
  medium: { width: 600, height: 350 },
  large: { width: 800, height: 450 },
  wide: { width: 1000, height: 400 },
} as const;

// Formatters
export const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

export const formatPercent = (value: number, decimals: number = 1): string => {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export const formatShortDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short' });
};
